import {memo, useMemo} from "react";
import {ClientDiagramNode} from "../../utility/types";
import {Label} from "./Label";
import {Badge, Box, HStack, Stack, Text} from "@chakra-ui/react";
import {PopoverRoot, PopoverTrigger, PopoverContent, PopoverBody} from "../../../../../../../../components/ui/popover";
import type {MockSourceEvidence} from "../../../../types";

export const NodeLabel = memo(
  ({node, citations, evidence}: {node: ClientDiagramNode; citations?: number[]; evidence?: MockSourceEvidence[]}) => {
    // Get all extractions with source info
    const allExtractions = useMemo(() => {
      if (!evidence) return [];
      return evidence.flatMap(e =>
        e.extractions.map(ext => ({...ext, sourceName: e.source_name, sourceId: e.source_id})),
      );
    }, [evidence]);

    const footer =
      citations && citations.length > 0 ? (
        <HStack gap={1} pt={1} borderTopWidth="1px" borderTopColor="#e2e8f0" mt={1} position="relative" zIndex={1000}>
          {citations.map(num => {
            // Get the extraction for this specific citation number
            const extraction = allExtractions.find(ext => ext.citation_number === num);

            if (!extraction) {
              // If no evidence available, render non-clickable badge
              return (
                <Badge key={num} colorPalette="blue" size="xs" css={{fontSize: "9px"}}>
                  {num}
                </Badge>
              );
            }

            // Render clickable badge with popover
            return (
              <PopoverRoot key={num} positioning={{placement: "top"}}>
                <PopoverTrigger asChild>
                  <Badge
                    colorPalette="blue"
                    size="xs"
                    css={{fontSize: "9px", cursor: "pointer", position: "relative", zIndex: 1000}}
                  >
                    {num}
                  </Badge>
                </PopoverTrigger>
                <PopoverContent maxW="md" css={{zIndex: 10000}}>
                  <PopoverBody>
                    <Stack gap={2}>
                      <Box>
                        <HStack gap={2} mb={1}>
                          <Badge colorPalette="blue" size="sm">
                            {extraction.citation_number}
                          </Badge>
                          <Text fontSize="xs" color="fg.muted" fontWeight="medium">
                            {extraction.sourceName}
                          </Text>
                        </HStack>
                        {extraction.page_reference && (
                          <Text fontSize="xs" color="fg.muted" mb={1}>
                            {extraction.page_reference}
                          </Text>
                        )}
                        <Text fontSize="sm">{extraction.quote}</Text>
                      </Box>
                    </Stack>
                  </PopoverBody>
                </PopoverContent>
              </PopoverRoot>
            );
          })}
        </HStack>
      ) : undefined;

    return (
      <Label label={node.label} description={node.description} coords={node.position} height={64} footer={footer} />
    );
  },
);
